import React, { useMemo, useState } from 'react'
import { useAppDispatch } from '@hooks/reduxHooks'
import {
  IconButton,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Button,
  Popover,
  Paper,
  Box,
  Typography,
} from '@mui/material'
import { CCAMDto, NGAPDto } from '@services/dtos'
import { doSearchCCAM, doSearchNGAP } from '@state/reducers/procedureReducer'
import AsyncAutoComplete from '@components/inputs/AsyncAutoComplete'
import { EditIcon } from 'lucide-react'

type Props = {
  onChange: (ev: {
    cotationType: 'NGAP' | 'CCAM'
    value: CCAMDto | NGAPDto
  }) => void
  value?: CCAMDto | NGAPDto
}

const CcamAndNgapSearchComponent: React.FC<Props> = ({ onChange, value }) => {
  const dispatch = useAppDispatch()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [cotationType, setCotationType] = useState<'CCAM' | 'NGAP'>('CCAM')
  const [result, setResult] = useState<
    { value: CCAMDto; label: string }[] | { value: NGAPDto; label: string }[]
  >([])
  const isOpen = Boolean(anchorEl)

  const options = useMemo(
    () =>
      result.map((res) => ({
        value: res.value,
        label: `${res.label} - ${res.value.code}`,
      })),
    [result],
  )

  const handleSearch = async (search: string, cotationType: string) => {
    let res: NGAPDto[] | CCAMDto[]
    if (search.trimStart() === '') {
      return
    }
    if (cotationType === 'CCAM') {
      res = await dispatch(
        doSearchCCAM({ libelle: search, examDate: new Date() }),
      ).unwrap()
    } else {
      res = await dispatch(
        doSearchNGAP({ libelle: search, examDate: new Date() }),
      ).unwrap()
    }
    setResult(
      res.map((r) => ({
        value: r,
        label: r.libelle,
      })),
    )
  }

  const onSearch = async (search: string) => {
    if (!search) {
      return
    }
    await handleSearch(search, cotationType)
  }

  const handleCotationTypeChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setCotationType(event.target.value as 'CCAM' | 'NGAP')
    setResult([])
  }

  return (
    <div>
      <IconButton
        size="small"
        color="info"
        onClick={(event) => setAnchorEl(event.currentTarget)}
        sx={{ p: 0.5 }}
      >
        <EditIcon size={17} />
      </IconButton>
      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{ elevation: 8 }}
      >
        <Paper sx={{ p: 2, width: 520, maxWidth: 'calc(100vw - 32px)' }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
            Sélectionner un acte
          </Typography>
          <FormControl component="fieldset">
            <FormLabel component="legend">Type de cotation</FormLabel>
            <RadioGroup
              aria-label="cotationType"
              row
              name="cotationType"
              value={cotationType}
              onChange={handleCotationTypeChange}
            >
              <FormControlLabel value="CCAM" control={<Radio />} label="CCAM" />
              <FormControlLabel value="NGAP" control={<Radio />} label="NGAP" />
            </RadioGroup>
          </FormControl>
          <AsyncAutoComplete
            getValue={(v) => v.code}
            options={options}
            value={value ? { value, label: value.libelle } : null}
            onChange={(v) => {
              onChange({ cotationType, value: v as CCAMDto | NGAPDto })
              setAnchorEl(null)
            }}
            handleSearch={onSearch}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button variant="outlined" onClick={() => setAnchorEl(null)}>
              Annuler
            </Button>
          </Box>
        </Paper>
      </Popover>
    </div>
  )
}

export default CcamAndNgapSearchComponent
